import React, { useState, useEffect } from 'react';
import { 
  Typography, 
  Box, 
  Card, 
  CardContent, 
  CircularProgress, 
  Alert,
  Paper,
  Tabs,
  Tab,
  Divider,
  Button
} from '@mui/material';
import { 
  BarChart as BarChartIcon,
  PieChart as PieChartIcon,
  TableChart as TableChartIcon,
  Assessment as AssessmentIcon
} from '@mui/icons-material';
import { analyzeDataModels, checkDataHygiene } from '../services/api';
import { DataModelAnalysis, HygieneSummary } from '../types';
import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  Legend, 
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
  AreaChart,
  Area,
  ScatterChart,
  Scatter,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  Radar
} from 'recharts';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel = (props: TabPanelProps) => {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`report-tabpanel-${index}`}
      aria-labelledby={`report-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
};

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8', '#82ca9d'];

// Report generation functions
const generateAnalysisReport = (analysis: DataModelAnalysis | null) => {
  if (!analysis) return;
  
  const reportContent = `
DATA MODEL ANALYSIS REPORT
Generated on: ${new Date().toLocaleDateString()}

=== ENTITY COMPARISON ===
${Object.entries(analysis.comparison.entity_comparison).map(([entity, comparison]) => `
${entity}:
- SFDC Fields: ${comparison.field_count.entity1}
- Affinity Fields: ${comparison.field_count.entity2}
- Common Fields: ${comparison.common_field_count}
- Primary Keys: ${comparison.primary_keys.entity1} (SFDC) / ${comparison.primary_keys.entity2} (Affinity)
`).join('')}

=== KEY DIFFERENCES ===
${Object.entries(analysis.comparison.key_differences).map(([entity, difference]) => `
${entity}: ${difference}`).join('')}

=== COMMON FIELDS ===
${Object.entries(analysis.comparison.common_fields).map(([entity, fields]) => `
${entity}:
${fields.map(field => `- ${field.entity1_field} ↔ ${field.entity2_field}`).join('\n')}
`).join('')}

=== UNIQUE FIELDS ===
SFDC Unique Fields:
${Object.entries(analysis.comparison.unique_fields.sfdc).map(([entity, fields]) => `
${entity}: ${fields.join(', ')}`).join('')}

Affinity Unique Fields:
${Object.entries(analysis.comparison.unique_fields.affinity).map(([entity, fields]) => `
${entity}: ${fields.join(', ')}`).join('')}

=== RELATIONSHIPS ===
SFDC Relationships:
${analysis.sfdc.relationships.map(rel => `- ${rel.from} → ${rel.to} (${rel.type})`).join('\n')}

Affinity Relationships:
${analysis.affinity.relationships.map(rel => `- ${rel.from} → ${rel.to} (${rel.type})`).join('\n')}
`;

  // Create and download the report
  const blob = new Blob([reportContent], { type: 'text/plain' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `data-model-analysis-report-${new Date().toISOString().split('T')[0]}.txt`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

const generateHygieneReport = (hygiene: HygieneSummary | null) => {
  if (!hygiene) return;
  
  const reportContent = `
DATA HYGIENE REPORT
Generated on: ${new Date().toLocaleDateString()}

=== SUMMARY ===
Total Issues: ${hygiene.total_issues}

=== ISSUES BY SOURCE ===
${Object.entries(hygiene.issues_by_source).map(([source, counts]) => `
${source}:
${Object.entries(counts).map(([entity, count]) => 
  entity !== 'total' ? `  ${entity}: ${count}` : `  Total: ${count}`
).join('\n')}
`).join('')}

=== ISSUES BY TYPE ===
${Object.entries(hygiene.issues_by_type).map(([type, count]) => `${type}: ${count}`).join('\n')}

=== RECOMMENDATIONS ===

HIGH PRIORITY:
${hygiene.recommendations.high_priority.map(rec => `- ${rec}`).join('\n')}

MEDIUM PRIORITY:
${hygiene.recommendations.medium_priority.map(rec => `- ${rec}`).join('\n')}

LOW PRIORITY:
${hygiene.recommendations.low_priority.map(rec => `- ${rec}`).join('\n')}

=== NEXT STEPS ===
1. Address high priority issues first
2. Implement data validation rules
3. Set up automated data quality monitoring
4. Schedule regular data hygiene reviews
5. Train team on data entry best practices
`;

  // Create and download the report
  const blob = new Blob([reportContent], { type: 'text/plain' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = `data-hygiene-report-${new Date().toISOString().split('T')[0]}.txt`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

const DataAnalysisHygiene: React.FC = () => {
  const [tabValue, setTabValue] = useState(0);
  const [modelAnalysis, setModelAnalysis] = useState<DataModelAnalysis | null>(null);
  const [hygieneSummary, setHygieneSummary] = useState<HygieneSummary | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Fetch data based on the active tab
        if (tabValue === 0) {
          // Data Model Analysis
          const analysis = await analyzeDataModels();
          setModelAnalysis(analysis);
        } else if (tabValue === 1) {
          // Data Hygiene Summary
          const summary = await checkDataHygiene();
          setHygieneSummary(summary);
        }
      } catch (err) {
        setError('Failed to fetch report data. Please try again later.');
        console.error('Error fetching report data:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [tabValue]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const renderDataModelAnalysis = () => {
    if (!modelAnalysis) return <Typography>No analysis data available</Typography>;

    // Prepare data for entity comparison chart
    const entityComparisonData = Object.entries(modelAnalysis.comparison.entity_comparison).map(([entity, comparison]) => ({
      name: entity,
      SFDC: comparison.field_count.entity1,
      Affinity: comparison.field_count.entity2,
      Common: comparison.common_field_count
    }));

    // Prepare data for field type distribution
    const fieldTypeData = Object.entries(modelAnalysis.sfdc.entities).flatMap(([entityName, entity]) => 
      Object.entries(entity.data_types).map(([field, type]) => ({
        entity: entityName,
        field,
        type
      }))
    ).reduce((acc, curr) => {
      acc[curr.type] = (acc[curr.type] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    const fieldTypeChartData = Object.entries(fieldTypeData).map(([type, count]) => ({
      name: type,
      value: count
    }));

    // Prepare data for relationship visualization
    const relationshipData = modelAnalysis.sfdc.relationships.map(rel => ({
      from: rel.from,
      to: rel.to,
      value: 1
    }));

    // Prepare time series data (mock data for demonstration)
    const timeSeriesData = [
      { month: 'Jan', SFDC: 4000, Affinity: 2400 },
      { month: 'Feb', SFDC: 3000, Affinity: 1398 },
      { month: 'Mar', SFDC: 2000, Affinity: 9800 },
      { month: 'Apr', SFDC: 2780, Affinity: 3908 },
      { month: 'May', SFDC: 1890, Affinity: 4800 },
      { month: 'Jun', SFDC: 2390, Affinity: 3800 },
      { month: 'Jul', SFDC: 3490, Affinity: 4300 },
    ];

    return (
      <Box>
        <Paper sx={{ p: 2, mb: 3 }}>
          <Typography variant="subtitle1" gutterBottom>
            Entity Comparison
          </Typography>
          
          {Object.entries(modelAnalysis.comparison.entity_comparison).map(([entity, comparison]) => (
            <Box key={entity} sx={{ mb: 2 }}>
              <Typography variant="subtitle2" fontWeight="bold">
                {entity}
              </Typography>
              <Box sx={{ pl: 2 }}>
                <Typography variant="body2">
                  SFDC Fields: {comparison.field_count.entity1}
                </Typography>
                <Typography variant="body2">
                  Affinity Fields: {comparison.field_count.entity2}
                </Typography>
                <Typography variant="body2">
                  Common Fields: {comparison.common_field_count}
                </Typography>
                <Typography variant="body2">
                  Primary Keys: {comparison.primary_keys.entity1} (SFDC) / {comparison.primary_keys.entity2} (Affinity)
                </Typography>
              </Box>
            </Box>
          ))}
        </Paper>
        
        <Paper sx={{ p: 2, mb: 3 }}>
          <Typography variant="subtitle1" gutterBottom>
            Key Differences
          </Typography>
          
          <Box component="ul" sx={{ pl: 2 }}>
            {Object.entries(modelAnalysis.comparison.key_differences).map(([entity, difference]) => (
              <Box component="li" key={entity}>
                <Typography variant="body2">
                  <strong>{entity}:</strong> {difference}
                </Typography>
              </Box>
            ))}
          </Box>
        </Paper>
        
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
          <Button 
            variant="contained" 
            color="primary"
            startIcon={<AssessmentIcon />}
            onClick={() => generateAnalysisReport(modelAnalysis)}
          >
            Generate Full Analysis Report
          </Button>
        </Box>
      </Box>
    );
  };

  const renderDataHygieneSummary = () => {
    if (!hygieneSummary) return <Typography>No hygiene data available</Typography>;

    // Prepare data for issues by type pie chart
    const issuesByTypeData = Object.entries(hygieneSummary.issues_by_type).map(([type, count]) => ({
      name: type,
      value: count
    }));

    // Prepare data for issues by source chart
    const issuesBySourceData = Object.entries(hygieneSummary.issues_by_source).map(([source, counts]) => ({
      name: source,
      value: counts.total
    }));

    // Prepare trend data (mock data for demonstration)
    const trendData = [
      { month: 'Jan', issues: 65 },
      { month: 'Feb', issues: 59 },
      { month: 'Mar', issues: 80 },
      { month: 'Apr', issues: 55 },
      { month: 'May', issues: 40 },
      { month: 'Jun', issues: 35 },
      { month: 'Jul', issues: 30 },
    ];

    // Prepare priority distribution data
    const priorityData = [
      { name: 'High', value: hygieneSummary.recommendations.high_priority.length },
      { name: 'Medium', value: hygieneSummary.recommendations.medium_priority.length },
      { name: 'Low', value: hygieneSummary.recommendations.low_priority.length },
    ];

    return (
      <Box>
        <Paper sx={{ p: 2, mb: 3 }}>
          <Typography variant="subtitle1" gutterBottom>
            Total Issues: {hygieneSummary.total_issues}
          </Typography>
          
          <Typography variant="subtitle2">Issues by Source:</Typography>
          <Box sx={{ pl: 2, mb: 2 }}>
            {Object.entries(hygieneSummary.issues_by_source).map(([source, counts]) => (
              <Box key={source}>
                <Typography variant="body2" fontWeight="bold">
                  {source}:
                </Typography>
                <Box sx={{ pl: 2 }}>
                  {Object.entries(counts).map(([entity, count]) => (
                    entity !== 'total' && (
                      <Typography key={entity} variant="body2">
                        {entity}: {count}
                      </Typography>
                    )
                  ))}
                  <Typography variant="body2" fontWeight="bold">
                    Total: {counts.total}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Box>
          
          <Typography variant="subtitle2">Issues by Type:</Typography>
          <Box sx={{ pl: 2, mb: 2 }}>
            {Object.entries(hygieneSummary.issues_by_type).map(([type, count]) => (
              <Typography key={type} variant="body2">
                {type}: {count}
              </Typography>
            ))}
          </Box>
        </Paper>
        
        <Paper sx={{ p: 2, mb: 3 }}>
          <Typography variant="subtitle1" gutterBottom>
            Recommendations
          </Typography>
          
          <Typography variant="subtitle2" color="error">
            High Priority:
          </Typography>
          <Box component="ul" sx={{ pl: 2, mb: 2 }}>
            {hygieneSummary.recommendations.high_priority.map((rec, index) => (
              <Box component="li" key={index}>
                <Typography variant="body2">{rec}</Typography>
              </Box>
            ))}
          </Box>
          
          <Typography variant="subtitle2" color="warning.main">
            Medium Priority:
          </Typography>
          <Box component="ul" sx={{ pl: 2, mb: 2 }}>
            {hygieneSummary.recommendations.medium_priority.map((rec, index) => (
              <Box component="li" key={index}>
                <Typography variant="body2">{rec}</Typography>
              </Box>
            ))}
          </Box>
          
          <Typography variant="subtitle2" color="success.main">
            Low Priority:
          </Typography>
          <Box component="ul" sx={{ pl: 2 }}>
            {hygieneSummary.recommendations.low_priority.map((rec, index) => (
              <Box component="li" key={index}>
                <Typography variant="body2">{rec}</Typography>
              </Box>
            ))}
          </Box>
        </Paper>
        
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
          <Button 
            variant="contained" 
            color="primary"
            startIcon={<TableChartIcon />}
            onClick={() => generateHygieneReport(hygieneSummary)}
          >
            Generate Full Hygiene Report
          </Button>
        </Box>
      </Box>
    );
  };

  return (
    <div>
      <Typography variant="h4" gutterBottom>
        Data Analysis & Hygiene
      </Typography>
      
      <Paper sx={{ width: '100%', mb: 2 }}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          indicatorColor="primary"
          textColor="primary"
          variant="fullWidth"
        >
          <Tab label="Data Model Analysis" />
          <Tab label="Data Hygiene" />
        </Tabs>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <TabPanel value={tabValue} index={0}>
            {renderDataModelAnalysis()}
          </TabPanel>

          <TabPanel value={tabValue} index={1}>
            {renderDataHygieneSummary()}
          </TabPanel>
        </>
      )}
    </div>
  );
};

export default DataAnalysisHygiene;
